import clientApi from '@/app/features/admin/clients/client-service';
import { Client } from '@/app/shared/types/models/client.interface';
import { DetailPage } from '@/app/features/admin/clients/detail.page.interface';
import { defineStore } from 'pinia';
import { TabHeader } from '@/app/shared/types/tab-header.interface';
import { PaginatedResponse } from '@/app/shared/types/api/api-response.interface';
import { ClientRequest } from './client-request.interface';
import { Datasource } from '@/app/shared/types/datasource.interface';
import datasourceApi from '@/app/features/admin/clients/datasource-service';

export const useClientStore = defineStore({
  id: 'client-store-id',
  state: () => ({
    clientDetails: new Map<string | number, DetailPage>(),
    client: {} as Client,
    clientList: {} as PaginatedResponse<Client>,
    tabs: [{ id: 0, name: 'All Clients' }] as TabHeader[],
    selectedTab: 0,
    dataSourceList: [] as Array<Datasource>,
  }),
  getters: {
    getClientById: (state) => (id: string | number) => {
      return state.clientDetails.get(id);
    },
  },
  actions: {
    async fetchClients(
      page = 0,
      size = 20,
      sortBy = 'id',
      sortDir: 'asc' | 'desc' = 'asc'
    ) {
      const {
        data: { data },
      } = await clientApi.getClients({ page, size, sortBy, sortDir });
      this.clientList = data;
    },

    async createClientTab(detailPage: DetailPage) {
      if (detailPage.isNew) {
        detailPage.clientData = {
          id: -1,
          clientName: '',
          description: '',
        };
        this.clientDetails.set(detailPage.id, detailPage);
      } else {
        const resp = await clientApi.getClientById(detailPage.id);
        detailPage.clientData = resp.data.data;
        this.clientDetails.set(detailPage.id, detailPage);
      }
      const tabIndex = this.tabs.findIndex(
        (tab: TabHeader) => tab.name === detailPage.name
      );
      if (tabIndex === -1) {
        this.tabs.push({
          id: detailPage.id,
          name: detailPage.name,
          autoLocked: false,
        });
        this.selectedTab = this.tabs.length - 1;
      } else {
        this.selectedTab = tabIndex;
      }
    },

    removeTab(selectedId: number) {
      const fIndex = this.tabs.findIndex((tab: any) => tab.id === selectedId);
      fIndex > 0 && this.tabs.splice(fIndex, 1);
    },

    closeTab(tabId: number | string) {
      const tabIndex = this.tabs.findIndex((tab) => tab.id === tabId);
      if (tabIndex !== -1 && tabIndex !== 0) {
        this.tabs.splice(tabIndex, 1);
        this.clientDetails.delete(tabId);
        if (this.selectedTab >= this.tabs.length) {
          this.selectedTab = this.tabs.length - 1;
        }
      }
    },

    selectTab(index: number) {
      this.selectedTab = index;
    },

    async deleteClient(id: number | string) {
      await clientApi.deleteClient(id);
      await this.fetchClients();
    },

    async getDataSourceList() {
      try {
        const {
          data: { data },
        } = await datasourceApi.getDataSources();

        this.dataSourceList = data;
        return this.dataSourceList;
      } catch (error) {
        this.dataSourceList = [];
        return this.dataSourceList;
      }
    },

    async addClient(client: ClientRequest) {
      await clientApi.addClient(client);
    },

    async updateClient(id: number, client: ClientRequest) {
      const response = await clientApi.updateClient(id, client);
      const detailPage = this.clientDetails.get(id);
      if (detailPage) {
        detailPage.clientData = response.data.data;
      }
      await this.fetchClients();
      return response.data.data;
    },
  },
});
